import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, Users, FileText, CheckCircle, XCircle, Trash2,
  Shield, Loader, Download, Clock, ChevronLeft,
  ChevronRight, UserCheck, UserX, Settings, Zap, Cloud, MessageSquare, Pencil,
} from 'lucide-react';
import api from '../services/api';
import NoteDocumentPreview from './NoteDocumentPreview';
import DriveImportPanel from './admin/DriveImportPanel';
import AdminUserEditModal from './admin/AdminUserEditModal';
import { invalidateCommunityLinks } from '../hooks/useCommunityLinks';

type Tab = 'dashboard' | 'notes' | 'users' | 'feedbacks' | 'settings' | 'quick' | 'drive';
type NoteFilter = 'all' | 'pending' | 'approved';

interface Stats {
  totalUsers: number;
  totalNotes: number;
  pendingNotes: number;
  approvedNotes: number;
  totalDownloads: number;
}

const AdminPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [stats, setStats] = useState<Stats | null>(null);
  const [notes, setNotes] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [feedbacks, setFeedbacks] = useState<any[]>([]);
  const [feedbacksPage, setFeedbacksPage] = useState(1);
  const [feedbacksPagination, setFeedbacksPagination] = useState<any>(null);
  const [editUser, setEditUser] = useState<any | null>(null);
  const [noteFilter, setNoteFilter] = useState<NoteFilter>('all');
  const [notesPage, setNotesPage] = useState(1);
  const [usersPage, setUsersPage] = useState(1);
  const [notesPagination, setNotesPagination] = useState<any>(null);
  const [usersPagination, setUsersPagination] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  const [uniText, setUniText] = useState('');
  const [deptText, setDeptText] = useState('');
  const [footerSubjectsJson, setFooterSubjectsJson] = useState('[]');
  const [footerResourcesJson, setFooterResourcesJson] = useState('[]');
  const [footerSupportJson, setFooterSupportJson] = useState('[]');
  const [whatsappLinksJson, setWhatsappLinksJson] = useState('[]');
  const [settingsMsg, setSettingsMsg] = useState<string | null>(null);

  const [qTitle, setQTitle] = useState('');
  const [qDesc, setQDesc] = useState('');
  const [qSubject, setQSubject] = useState('matematik');
  const [qUniversity, setQUniversity] = useState('');
  const [qYear, setQYear] = useState('sinif1');
  const [qDept, setQDept] = useState('');
  const [qFileUrl, setQFileUrl] = useState('');
  const [qFileName, setQFileName] = useState('');
  const [qFileSize, setQFileSize] = useState('0');
  const [qAuto, setQAuto] = useState(true);
  const [quickMsg, setQuickMsg] = useState<string | null>(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const google = params.get('google');
    if (!google) return;

    setTab('drive');
    if (google === 'connected') {
      setSettingsMsg('Google Drive bağlantısı başarılı!');
    } else if (google === 'error') {
      setSettingsMsg('Google bağlantısı tamamlanamadı. Tekrar deneyin.');
    }
    window.history.replaceState({}, '', `${window.location.pathname}`);
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.adminGetStats();
      if (res.success) setStats(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page: notesPage, limit: 10 };
      if (noteFilter !== 'all') params.status = noteFilter;
      const res = await api.adminGetNotes(params);
      if (res.success) {
        setNotes(res.data.notes);
        setNotesPagination(res.data.pagination);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [notesPage, noteFilter]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.adminGetUsers({ page: usersPage, limit: 15 });
      if (res.success) {
        setUsers(res.data.users);
        setUsersPagination(res.data.pagination);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [usersPage]);

  const fetchFeedbacks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.adminGetFeedbacks({ page: feedbacksPage, limit: 15 });
      if (res.success) {
        setFeedbacks(res.data.items || []);
        setFeedbacksPagination(res.data.pagination);
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [feedbacksPage]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { if (tab === 'notes') fetchNotes(); }, [tab, fetchNotes]);
  useEffect(() => { if (tab === 'users') fetchUsers(); }, [tab, fetchUsers]);
  useEffect(() => { if (tab === 'feedbacks') fetchFeedbacks(); }, [tab, fetchFeedbacks]);

  const loadSiteSettings = useCallback(async () => {
    try {
      const res = await api.adminGetSiteSettings();
      if (res.success && res.data?.settings) {
        const s = res.data.settings;
        setUniText((s.universities || []).join('\n'));
        setDeptText((s.departments || []).join('\n'));
        setFooterSubjectsJson(JSON.stringify(s.footerSubjects || [], null, 2));
        setFooterResourcesJson(JSON.stringify(s.footerResources || [], null, 2));
        setFooterSupportJson(JSON.stringify(s.footerSupport || [], null, 2));
        setWhatsappLinksJson(JSON.stringify(s.whatsappLinks || [], null, 2));
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    if (tab === 'settings' || tab === 'quick') loadSiteSettings();
  }, [tab, loadSiteSettings]);

  const saveSiteSettings = async () => {
    setSettingsMsg(null);
    try {
      let footerSubjects: any[] = [];
      let footerResources: any[] = [];
      let footerSupport: any[] = [];
      let whatsappLinks: any[] = [];
      try {
        footerSubjects = JSON.parse(footerSubjectsJson);
        footerResources = JSON.parse(footerResourcesJson);
        footerSupport = JSON.parse(footerSupportJson);
        whatsappLinks = JSON.parse(whatsappLinksJson);
      } catch {
        setSettingsMsg('JSON geçersiz (footer veya WhatsApp)');
        return;
      }
      const res = await api.adminPutSiteSettings({
        universities: uniText.split('\n').map((l) => l.trim()).filter(Boolean),
        departments: deptText.split('\n').map((l) => l.trim()).filter(Boolean),
        footerSubjects,
        footerResources,
        footerSupport,
        whatsappLinks,
      });
      if (res.success) {
        setSettingsMsg('Kaydedildi');
        invalidateCommunityLinks();
      } else setSettingsMsg(res.error || 'Hata');
    } catch (e: any) {
      setSettingsMsg(e.message || 'Hata');
    }
  };

  const submitQuickNote = async () => {
    setQuickMsg(null);
    try {
      const res = await api.adminQuickAddNotes({
        autoApprove: qAuto,
        notes: [{
          title: qTitle,
          description: qDesc,
          subject: qSubject,
          university: qUniversity,
          universityYear: qYear,
          department: qDept || undefined,
          fileUrl: qFileUrl,
          fileName: qFileName,
          fileSize: parseInt(qFileSize, 10) || 0,
        }],
      });
      if (res.success) {
        setQuickMsg(`${res.data.count} not eklendi`);
        setQTitle(''); setQDesc(''); setQFileUrl(''); setQFileName('');
        fetchStats();
      } else setQuickMsg(res.error || 'Hata');
    } catch (e: any) {
      setQuickMsg(e.message || 'Hata');
    }
  };

  const handleApprove = async (id: string) => {
    setActionLoading(id);
    try { await api.adminApproveNote(id); fetchNotes(); fetchStats(); }
    catch (err) { console.error(err); }
    setActionLoading(null);
  };

  const handleReject = async (id: string) => {
    setActionLoading(id);
    try { await api.adminRejectNote(id); fetchNotes(); fetchStats(); }
    catch (err) { console.error(err); }
    setActionLoading(null);
  };

  const handleDeleteNote = async (id: string) => {
    if (!window.confirm('Bu notu kalıcı olarak silmek istediğinize emin misiniz?')) return;
    setActionLoading(id);
    try { await api.adminDeleteNote(id); fetchNotes(); fetchStats(); }
    catch (err) { console.error(err); }
    setActionLoading(null);
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    setActionLoading(userId);
    try { await api.adminChangeRole(userId, newRole); fetchUsers(); fetchStats(); }
    catch (err) { console.error(err); }
    setActionLoading(null);
  };

  const tabs: { id: Tab; label: string; icon: React.FC<any> }[] = [
    { id: 'dashboard', label: 'Panel', icon: LayoutDashboard },
    { id: 'drive', label: 'Drive Import', icon: Cloud },
    { id: 'notes', label: 'Notlar', icon: FileText },
    { id: 'feedbacks', label: 'Geri Bildirim', icon: MessageSquare },
    { id: 'users', label: 'Kullanıcılar', icon: Users },
    { id: 'settings', label: 'Site', icon: Settings },
    { id: 'quick', label: 'Hızlı Ekle', icon: Zap },
  ];

  const statCards = stats ? [
    { label: 'Toplam Kullanıcı', value: stats.totalUsers, icon: Users, color: '#06b6d4' },
    { label: 'Toplam Not', value: stats.totalNotes, icon: FileText, color: 'var(--accent-primary)' },
    { label: 'Onay Bekleyen', value: stats.pendingNotes, icon: Clock, color: '#f59e0b' },
    { label: 'Onaylı Not', value: stats.approvedNotes, icon: CheckCircle, color: '#10b981' },
    { label: 'Toplam İndirme', value: stats.totalDownloads, icon: Download, color: '#8b5cf6' },
  ] : [];

  const formatDate = (d: string) => new Date(d).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        minHeight: '100vh',
        paddingTop: isMobile ? '70px' : '80px',
        paddingBottom: '40px',
        position: 'relative',
        zIndex: 2,
      }}
    >
      <div style={{ maxWidth: '1300px', margin: '0 auto', padding: isMobile ? '0 12px' : '0 24px' }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: '32px', flexWrap: 'wrap' as const, gap: '16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '44px', height: '44px', borderRadius: '14px',
              background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 8px 25px rgba(139, 92, 246, 0.3)',
            }}>
              <Shield size={22} color="white" />
            </div>
            <div>
              <h1 style={{
                fontSize: isMobile ? '1.3rem' : '1.6rem', fontWeight: '800', margin: 0,
                fontFamily: "'Space Grotesk', sans-serif",
              }}>
                <span className="gradient-text-white">Admin Paneli</span>
              </h1>
              <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>Yönetim ve moderasyon</p>
            </div>
          </div>

          <motion.button
            onClick={onClose}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              background: 'rgba(51, 65, 85, 0.3)', border: '1px solid rgba(51, 65, 85, 0.2)',
              color: '#94a3b8', padding: '10px 20px', borderRadius: '12px',
              fontSize: '14px', fontWeight: '600', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '8px',
            }}
          >
            Ana Sayfaya Dön
          </motion.button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', gap: '6px', marginBottom: '28px',
          background: 'rgba(15, 23, 42, 0.5)', borderRadius: '14px', padding: '4px',
          border: '1px solid rgba(51, 65, 85, 0.15)', overflowX: 'auto' as const,
        }}>
          {tabs.map((t) => {
            const Icon = t.icon;
            const isActive = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                style={{
                  flex: 1, padding: isMobile ? '10px 12px' : '12px 20px', borderRadius: '10px',
                  border: 'none',
                  background: isActive ? 'rgba(139, 92, 246, 0.15)' : 'transparent',
                  color: isActive ? '#a78bfa' : '#64748b',
                  fontWeight: '600', fontSize: '14px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  transition: 'all 0.3s ease', whiteSpace: 'nowrap' as const,
                }}
              >
                <Icon size={16} />
                <span>{t.label}</span>
                {t.id === 'notes' && stats && stats.pendingNotes > 0 && (
                  <span style={{
                    background: '#f59e0b', color: '#000', fontSize: '11px', fontWeight: '700',
                    padding: '2px 7px', borderRadius: '10px', minWidth: '18px', textAlign: 'center' as const,
                  }}>
                    {stats.pendingNotes}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Dashboard Tab */}
        {tab === 'dashboard' && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: isMobile ? '10px' : '16px',
          }}>
            {statCards.map((card) => {
              const Icon = card.icon;
              return (
                <motion.div
                  key={card.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glow-card"
                  style={{ padding: isMobile ? '16px' : '24px', borderRadius: '16px' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '10px',
                      background: `${card.color}15`, display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon size={18} color={card.color} />
                    </div>
                  </div>
                  <div style={{
                    fontSize: isMobile ? '1.5rem' : '2rem', fontWeight: '800', color: card.color,
                    fontFamily: "'Space Grotesk', sans-serif",
                  }}>
                    {card.value.toLocaleString('tr-TR')}
                  </div>
                  <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '500', marginTop: '2px' }}>
                    {card.label}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Notes Tab */}
        {tab === 'notes' && (
          <div>
            <div style={{ display: 'flex', gap: '6px', marginBottom: '20px', flexWrap: 'wrap' as const }}>
              {(['all', 'pending', 'approved'] as NoteFilter[]).map((f) => (
                <button
                  key={f}
                  onClick={() => { setNoteFilter(f); setNotesPage(1); }}
                  style={{
                    padding: '8px 16px', borderRadius: '10px', border: 'none',
                    background: noteFilter === f ? 'rgba(139, 92, 246, 0.15)' : 'rgba(30, 41, 59, 0.4)',
                    color: noteFilter === f ? '#a78bfa' : '#64748b',
                    fontWeight: '600', fontSize: '13px', cursor: 'pointer',
                  }}
                >
                  {f === 'all' ? 'Tümü' : f === 'pending' ? 'Bekleyen' : 'Onaylı'}
                </button>
              ))}
            </div>

            {loading ? (
              <div style={{ textAlign: 'center' as const, padding: '60px 0' }}>
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
                  <Loader size={28} color="#8b5cf6" />
                </motion.div>
              </div>
            ) : notes.length === 0 ? (
              <div className="glow-card" style={{ padding: '48px', borderRadius: '16px', textAlign: 'center' as const }}>
                <FileText size={40} color="#334155" style={{ marginBottom: '12px' }} />
                <p style={{ color: '#64748b' }}>Bu filtrede not bulunmuyor</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '12px' }}>
                {notes.map((note) => (
                  <motion.div
                    key={note._id}
                    layout
                    className="glow-card"
                    style={{
                      padding: isMobile ? '16px' : '20px', borderRadius: '16px',
                      display: 'flex', flexDirection: isMobile ? 'column' as const : 'row' as const,
                      alignItems: isMobile ? 'stretch' : 'center',
                      gap: isMobile ? '12px' : '16px',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' as const }}>
                        <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: 'white' }}>{note.title}</h4>
                        <span style={{
                          padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600',
                          background: note.isApproved ? 'var(--accent-primary-soft)' : 'rgba(245, 158, 11, 0.1)',
                          color: note.isApproved ? 'var(--accent-primary)' : '#f59e0b',
                          border: `1px solid ${note.isApproved ? 'var(--accent-primary-border)' : 'rgba(245, 158, 11, 0.2)'}`,
                        }}>
                          {note.isApproved ? 'Onaylı' : 'Bekliyor'}
                        </span>
                      </div>
                      <p style={{ color: '#64748b', fontSize: '12px', margin: 0 }}>
                        {note.author?.name || 'Anonim'} · {note.subject}
                        {note.university ? ` · ${note.university}` : ''}
                        {note.universityYear ? ` · ${note.universityYear}` : ''} · {formatDate(note.createdAt)} · {note.downloads} indirme
                      </p>
                    </div>

                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0, flexWrap: 'wrap' as const }}>
                      {!note.isApproved && (
                        <motion.button
                          onClick={() => handleApprove(note._id)}
                          disabled={actionLoading === note._id}
                          whileTap={{ scale: 0.95 }}
                          style={{
                            background: 'var(--accent-primary-soft)', border: '1px solid var(--accent-primary-border)',
                            color: 'var(--accent-primary)', padding: '8px 14px', borderRadius: '8px',
                            fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '4px',
                          }}
                        >
                          <CheckCircle size={14} /> Onayla
                        </motion.button>
                      )}
                      {note.isApproved && (
                        <motion.button
                          onClick={() => handleReject(note._id)}
                          disabled={actionLoading === note._id}
                          whileTap={{ scale: 0.95 }}
                          style={{
                            background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.2)',
                            color: '#f59e0b', padding: '8px 14px', borderRadius: '8px',
                            fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '4px',
                          }}
                        >
                          <XCircle size={14} /> Reddet
                        </motion.button>
                      )}
                      <motion.button
                        onClick={() => handleDeleteNote(note._id)}
                        disabled={actionLoading === note._id}
                        whileTap={{ scale: 0.95 }}
                        style={{
                          background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)',
                          color: '#ef4444', padding: '8px 14px', borderRadius: '8px',
                          fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: '4px',
                        }}
                      >
                        <Trash2 size={14} /> Sil
                      </motion.button>
                    </div>
                  </motion.div>
                ))}

                {notesPagination && notesPagination.pages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
                    <button
                      disabled={notesPage <= 1}
                      onClick={() => setNotesPage(p => p - 1)}
                      style={{
                        padding: '8px 12px', borderRadius: '8px', border: 'none',
                        background: 'rgba(51, 65, 85, 0.3)', color: '#94a3b8',
                        cursor: notesPage <= 1 ? 'not-allowed' : 'pointer', opacity: notesPage <= 1 ? 0.5 : 1,
                      }}
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <span style={{ color: '#94a3b8', fontSize: '14px', padding: '8px 12px' }}>
                      {notesPage} / {notesPagination.pages}
                    </span>
                    <button
                      disabled={notesPage >= notesPagination.pages}
                      onClick={() => setNotesPage(p => p + 1)}
                      style={{
                        padding: '8px 12px', borderRadius: '8px', border: 'none',
                        background: 'rgba(51, 65, 85, 0.3)', color: '#94a3b8',
                        cursor: notesPage >= notesPagination.pages ? 'not-allowed' : 'pointer',
                        opacity: notesPage >= notesPagination.pages ? 0.5 : 1,
                      }}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {tab === 'users' && (
          <div>
            {loading ? (
              <div style={{ textAlign: 'center' as const, padding: '60px 0' }}>
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
                  <Loader size={28} color="#8b5cf6" />
                </motion.div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '10px' }}>
                {users.map((u) => (
                  <motion.div
                    key={u._id}
                    layout
                    className="glow-card"
                    style={{
                      padding: isMobile ? '14px' : '18px', borderRadius: '14px',
                      display: 'flex', alignItems: isMobile ? 'flex-start' : 'center',
                      flexDirection: isMobile ? 'column' as const : 'row' as const,
                      gap: '12px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                      <img
                        src={u.avatar}
                        alt={u.name}
                        style={{ width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0 }}
                      />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' as const }}>
                          <span style={{ fontWeight: '600', color: 'white', fontSize: '14px' }}>{u.name}</span>
                          <span style={{
                            padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '700',
                            background: u.role === 'admin' ? 'rgba(139, 92, 246, 0.15)' : 'rgba(51, 65, 85, 0.3)',
                            color: u.role === 'admin' ? '#a78bfa' : '#64748b',
                          }}>
                            {u.role === 'admin' ? 'Admin' : 'Kullanıcı'}
                          </span>
                          {u.isBanned && (
                            <span style={{
                              padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '700',
                              background: 'rgba(239,68,68,0.15)', color: '#f87171',
                            }}>
                              Kapatıldı
                            </span>
                          )}
                        </div>
                        <p style={{ color: '#475569', fontSize: '12px', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {u.email} · {formatDate(u.createdAt)}
                        </p>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0, flexWrap: 'wrap' as const }}>
                      <motion.button
                        onClick={() => setEditUser(u)}
                        disabled={actionLoading === u._id}
                        whileTap={{ scale: 0.95 }}
                        style={{
                          background: 'var(--accent-primary-soft)',
                          border: '1px solid var(--accent-primary-border)',
                          color: '#4ade80',
                          padding: '7px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '600',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                        }}
                      >
                        <Pencil size={13} /> Düzenle
                      </motion.button>
                      <motion.button
                        onClick={() => handleRoleChange(u._id, u.role === 'admin' ? 'user' : 'admin')}
                        disabled={actionLoading === u._id}
                        whileTap={{ scale: 0.95 }}
                        style={{
                          background: u.role === 'admin' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(139, 92, 246, 0.1)',
                          border: `1px solid ${u.role === 'admin' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(139, 92, 246, 0.2)'}`,
                          color: u.role === 'admin' ? '#f59e0b' : '#a78bfa',
                          padding: '7px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '600',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                        }}
                      >
                        {u.role === 'admin' ? <><UserX size={13} /> Yetkiyi Al</> : <><UserCheck size={13} /> Admin Yap</>}
                      </motion.button>
                    </div>
                  </motion.div>
                ))}

                {usersPagination && usersPagination.pages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
                    <button
                      disabled={usersPage <= 1}
                      onClick={() => setUsersPage(p => p - 1)}
                      style={{
                        padding: '8px 12px', borderRadius: '8px', border: 'none',
                        background: 'rgba(51, 65, 85, 0.3)', color: '#94a3b8',
                        cursor: usersPage <= 1 ? 'not-allowed' : 'pointer', opacity: usersPage <= 1 ? 0.5 : 1,
                      }}
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <span style={{ color: '#94a3b8', fontSize: '14px', padding: '8px 12px' }}>
                      {usersPage} / {usersPagination.pages}
                    </span>
                    <button
                      disabled={usersPage >= usersPagination.pages}
                      onClick={() => setUsersPage(p => p + 1)}
                      style={{
                        padding: '8px 12px', borderRadius: '8px', border: 'none',
                        background: 'rgba(51, 65, 85, 0.3)', color: '#94a3b8',
                        cursor: usersPage >= usersPagination.pages ? 'not-allowed' : 'pointer',
                        opacity: usersPage >= usersPagination.pages ? 0.5 : 1,
                      }}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Feedbacks Tab */}
        {tab === 'feedbacks' && (
          <div>
            {loading ? (
              <div style={{ textAlign: 'center' as const, padding: '60px 0' }}>
                <Loader size={28} color="#fbbf24" className="spin" />
              </div>
            ) : feedbacks.length === 0 ? (
              <p style={{ color: '#64748b', textAlign: 'center' as const, padding: 40 }}>Henüz düşük puanlı geri bildirim yok.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 10 }}>
                {feedbacks.map((fb) => (
                  <motion.div
                    key={fb._id}
                    className="glow-card"
                    style={{ padding: 18, borderRadius: 14 }}
                  >
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <img
                        src={fb.user?.avatar || 'https://ui-avatars.com/api/?name=U'}
                        alt=""
                        style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 8, alignItems: 'center', marginBottom: 6 }}>
                          <span style={{ color: '#f8fafc', fontWeight: 600 }}>{fb.user?.name || 'Anonim'}</span>
                          <span style={{ color: '#64748b', fontSize: 12 }}>{fb.user?.email}</span>
                          <span style={{ color: '#fbbf24', fontSize: 12, fontWeight: 700 }}>{fb.rating}/5 ★</span>
                        </div>
                        <p style={{ margin: '0 0 8px', color: '#cbd5e1', fontSize: 13, lineHeight: 1.6 }}>{fb.feedback}</p>
                        <p style={{ margin: 0, color: '#64748b', fontSize: 12 }}>
                          Not: <strong style={{ color: '#94a3b8' }}>{fb.note?.title || '—'}</strong>
                          {' · '}{formatDate(fb.createdAt)}
                        </p>
                        {fb.user?._id && (
                          <button
                            type="button"
                            onClick={() => setEditUser(fb.user)}
                            style={{
                              marginTop: 10,
                              background: 'transparent',
                              border: 'none',
                              color: '#a78bfa',
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: 'pointer',
                              padding: 0,
                            }}
                          >
                            Kullanıcı profilini aç →
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}

                {feedbacksPagination && feedbacksPagination.pages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                    <button
                      disabled={feedbacksPage <= 1}
                      onClick={() => setFeedbacksPage((p) => p - 1)}
                      style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: 'rgba(51,65,85,0.3)', color: '#94a3b8', cursor: feedbacksPage <= 1 ? 'not-allowed' : 'pointer' }}
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <span style={{ color: '#94a3b8', fontSize: 14, padding: '8px 12px' }}>
                      {feedbacksPage} / {feedbacksPagination.pages}
                    </span>
                    <button
                      disabled={feedbacksPage >= feedbacksPagination.pages}
                      onClick={() => setFeedbacksPage((p) => p + 1)}
                      style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: 'rgba(51,65,85,0.3)', color: '#94a3b8', cursor: feedbacksPage >= feedbacksPagination.pages ? 'not-allowed' : 'pointer' }}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {tab === 'settings' && (
          <div className="glow-card" style={{ padding: isMobile ? '16px' : '24px', borderRadius: '16px' }}>
            <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '18px', lineHeight: 1.55 }}>
              Üniversite ve bölüm listeleri her satırda bir kayıt. Footer sütunları{' '}
              <code style={{ color: '#94a3b8' }}>[{`{"label":"...","href":"#forum"}`}]</code> biçiminde JSON olmalıdır.
            </p>
            <div style={{ marginBottom: '14px' }}>
              <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>Üniversiteler</div>
              <textarea
                value={uniText}
                onChange={(e) => setUniText(e.target.value)}
                rows={7}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '12px', borderRadius: '12px',
                  border: '1px solid rgba(51, 65, 85, 0.35)', background: 'rgba(15, 23, 42, 0.5)', color: '#e2e8f0',
                  fontFamily: 'monospace', fontSize: '13px',
                }}
              />
            </div>
            <div style={{ marginBottom: '14px' }}>
              <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>Bölümler</div>
              <textarea
                value={deptText}
                onChange={(e) => setDeptText(e.target.value)}
                rows={7}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '12px', borderRadius: '12px',
                  border: '1px solid rgba(51, 65, 85, 0.35)', background: 'rgba(15, 23, 42, 0.5)', color: '#e2e8f0',
                  fontFamily: 'monospace', fontSize: '13px',
                }}
              />
            </div>
            <div style={{ marginBottom: '14px' }}>
              <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>Footer — Dersler (JSON)</div>
              <textarea
                value={footerSubjectsJson}
                onChange={(e) => setFooterSubjectsJson(e.target.value)}
                rows={5}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '12px', borderRadius: '12px',
                  border: '1px solid rgba(51, 65, 85, 0.35)', background: 'rgba(15, 23, 42, 0.5)', color: '#e2e8f0',
                  fontFamily: 'monospace', fontSize: '12px',
                }}
              />
            </div>
            <div style={{ marginBottom: '14px' }}>
              <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>Footer — Kaynaklar (JSON)</div>
              <textarea
                value={footerResourcesJson}
                onChange={(e) => setFooterResourcesJson(e.target.value)}
                rows={6}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '12px', borderRadius: '12px',
                  border: '1px solid rgba(51, 65, 85, 0.35)', background: 'rgba(15, 23, 42, 0.5)', color: '#e2e8f0',
                  fontFamily: 'monospace', fontSize: '12px',
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>Footer — Destek (JSON)</div>
              <textarea
                value={footerSupportJson}
                onChange={(e) => setFooterSupportJson(e.target.value)}
                rows={6}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '12px', borderRadius: '12px',
                  border: '1px solid rgba(51, 65, 85, 0.35)', background: 'rgba(15, 23, 42, 0.5)', color: '#e2e8f0',
                  fontFamily: 'monospace', fontSize: '12px',
                }}
              />
            </div>
            <div style={{
              marginBottom: '16px', paddingTop: '16px',
              borderTop: '1px solid rgba(37, 211, 102, 0.18)',
            }}>
              <div style={{ color: '#4ade80', fontSize: '12px', fontWeight: 700, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                WhatsApp Toplulukları (JSON)
              </div>
              <p style={{ color: '#64748b', fontSize: '12px', margin: '0 0 8px', lineHeight: 1.5 }}>
                Navbar, anasayfa ve giriş yapan kullanıcıya gösterilen pop-up'ta görünür.{' '}
                <code style={{ color: '#86efac' }}>
                  [{`{"label":"Genel Sohbet","href":"https://chat.whatsapp.com/...","description":"Herkese açık grup"}`}]
                </code>{' '}
                biçiminde. <code style={{ color: '#94a3b8' }}>description</code> isteğe bağlıdır.
              </p>
              <textarea
                value={whatsappLinksJson}
                onChange={(e) => setWhatsappLinksJson(e.target.value)}
                rows={7}
                placeholder='[{"label":"Genel Sohbet","href":"https://chat.whatsapp.com/xxxx","description":"Topluluk grubu"}]'
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '12px', borderRadius: '12px',
                  border: '1px solid rgba(37, 211, 102, 0.3)', background: 'rgba(15, 23, 42, 0.5)', color: '#e2e8f0',
                  fontFamily: 'monospace', fontSize: '12px',
                }}
              />
            </div>
            <motion.button
              type="button"
              whileTap={{ scale: 0.98 }}
              onClick={saveSiteSettings}
              style={{
                padding: '12px 22px', borderRadius: '12px', border: 'none',
                background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', color: 'white', fontWeight: 700, cursor: 'pointer',
              }}
            >
              Ayarları kaydet
            </motion.button>
            {settingsMsg && (
              <p style={{ marginTop: '12px', color: settingsMsg === 'Kaydedildi' ? 'var(--accent-primary)' : '#f87171', fontSize: '14px' }}>
                {settingsMsg}
              </p>
            )}
          </div>
        )}

        {tab === 'drive' && <DriveImportPanel />}

        {tab === 'quick' && (
          <div className="glow-card" style={{ padding: isMobile ? '16px' : '24px', borderRadius: '16px' }}>
            <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '16px' }}>
              Tek notu doğrudan ekleyin (onay kutusu işliyse otomatik onaylı). Üniversite / sınıf alanları üretimdeki not modeliyle uyumludur.
            </p>
            <div style={{ display: 'grid', gap: '12px', maxWidth: '560px' }}>
              <input value={qTitle} onChange={(e) => setQTitle(e.target.value)} placeholder="Başlık (min 5 karakter)" style={quickInput} />
              <textarea value={qDesc} onChange={(e) => setQDesc(e.target.value)} placeholder="Açıklama (min 10 karakter)" rows={3} style={quickInput} />
              <select value={qSubject} onChange={(e) => setQSubject(e.target.value)} style={quickInput}>
                {['matematik', 'fizik', 'kimya', 'biyoloji', 'turkce', 'tarih', 'cografya', 'felsefe', 'edebiyat', 'diger'].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <input value={qUniversity} onChange={(e) => setQUniversity(e.target.value)} placeholder="Üniversite adı" style={quickInput} />
              <select value={qYear} onChange={(e) => setQYear(e.target.value)} style={quickInput}>
                {[
                  ['hazirlik', 'Hazırlık'], ['sinif1', '1. Sınıf'], ['sinif2', '2. Sınıf'], ['sinif3', '3. Sınıf'],
                  ['sinif4', '4. Sınıf'], ['yuksek_lisans', 'Yüksek Lisans'], ['doktora', 'Doktora'], ['mezun', 'Mezun'],
                ].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <input value={qDept} onChange={(e) => setQDept(e.target.value)} placeholder="Bölüm (isteğe bağlı)" style={quickInput} />
              <input value={qFileUrl} onChange={(e) => setQFileUrl(e.target.value)} placeholder="Dosya veya Drive bağlantısı (URL)" style={quickInput} />
              {qFileUrl.trim() ? (
                <div style={{ maxWidth: '420px' }}>
                  <p style={{ color: '#64748b', fontSize: '12px', margin: '0 0 8px' }}>Yüklemeden önce önizleme</p>
                  <NoteDocumentPreview fileUrl={qFileUrl} fileName={qFileName || 'not.pdf'} height={200} interactive />
                </div>
              ) : null}
              <input value={qFileName} onChange={(e) => setQFileName(e.target.value)} placeholder="Dosya adı" style={quickInput} />
              <input value={qFileSize} onChange={(e) => setQFileSize(e.target.value)} placeholder="Dosya boyutu (byte, 0 olabilir)" style={quickInput} />
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#94a3b8', fontSize: '14px' }}>
                <input type="checkbox" checked={qAuto} onChange={(e) => setQAuto(e.target.checked)} />
                Otomatik onayla
              </label>
              <motion.button
                type="button"
                whileTap={{ scale: 0.98 }}
                onClick={submitQuickNote}
                style={{
                  padding: '12px 22px', borderRadius: '12px', border: 'none', justifySelf: 'start',
                  background: 'var(--gradient-primary)', color: 'white', fontWeight: 700, cursor: 'pointer',
                }}
              >
                Notu ekle
              </motion.button>
              {quickMsg && <p style={{ color: '#94a3b8', fontSize: '14px' }}>{quickMsg}</p>}
            </div>
          </div>
        )}
      </div>

      {editUser && (
        <AdminUserEditModal
          user={editUser}
          onClose={() => setEditUser(null)}
          onUpdated={() => {
            fetchUsers();
            if (tab === 'feedbacks') fetchFeedbacks();
          }}
        />
      )}
    </motion.div>
  );
};

const quickInput: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '12px',
  borderRadius: '12px',
  border: '1px solid rgba(51, 65, 85, 0.35)',
  background: 'rgba(15, 23, 42, 0.5)',
  color: '#e2e8f0',
  fontSize: '14px',
};

export default AdminPanel;
